import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Utils from '../../common/Utils';
import { Platform, View } from 'react-native';
import AppConstants from '../../common/Constants';
import LayoutContext from '../../contexts/Layout';

/**
 * HOC to pass hard dimension number values to components that cannot leverage dynamic string dimensions eg v*, %
 * @param {string, number} width
 * @returns {number}
 */
const HardDimensionsContainer = props => {
    const TIMEOUT_DURATION = 100;
    const DEFAULT_HARD_SIZE = 0.9;

    const { layout } = useContext(LayoutContext);
    const containerRef = useRef(null);
    const timeout = useRef(-1);
    const [containerRefVal, setContainerVal] = useState();
    const [width] = useState(props.width || '100%');
    const [height] = useState(props.height || '0.1%');
    const [offsetWidth, setOffsetWidth] = useState(DEFAULT_HARD_SIZE);
    const [offsetHeight, setOffsetHeight] = useState(DEFAULT_HARD_SIZE);
    const [dimensions, setDimensions] = useState({ width: width, height: height, hardWidth: 0, hardHeight: 0 });

    useMemo(() => {
        setDimensions({
            width: width,
            height: height,
            hardWidth: Utils.convertStylePropStringToNumerical(offsetWidth, 'width', containerRef),
            hardHeight: Utils.convertStylePropStringToNumerical(offsetHeight, 'height', containerRef)
        });
    }, [width, height, offsetWidth, offsetHeight]);

    const getDimensions = useCallback(() => dimensions, [dimensions]);

    const ro = useRef(
        Platform.OS === 'web' &&
            new window.ResizeObserver(() => {
                if (!containerRef.current) return;

                clearTimeout(timeout.current); // todo: manual resize still laggy @jacksonkr
                timeout.current = setTimeout(() => {
                    setOffsetWidth(containerRef.current.offsetWidth);
                    setOffsetHeight(containerRef.current.offsetHeight);
                }, TIMEOUT_DURATION);
            })
    );

    const layoutCallback = ne => {
        setOffsetWidth(Math.ceil(ne.nativeEvent.layout.width));
        setOffsetHeight(Math.ceil(ne.nativeEvent.layout.height));
    };

    useEffect(() => {
        if (containerRef.current) setContainerVal(containerRef.current);
        getDimensions();
    }, [containerRef, getDimensions]);

    const onElasticFin = () => {
        getDimensions();
    };

    useEffect(() => {
        if (Platform.OS !== 'web') return;

        window.addEventListener(AppConstants.WINDOW_EVENT_ELASTIC_ANIM_FINISH, onElasticFin);
        ro.current.observe(containerRef.current);

        return () => {
            clearTimeout(timeout.current);
            window.removeEventListener(AppConstants.WINDOW_EVENT_ELASTIC_ANIM_FINISH, onElasticFin);
            ro.current.disconnect(); // eslint-disable-line react-hooks/exhaustive-deps
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const reChildren = React.Children.map(props.children, child => {
        if (!child) return null;
        return React.cloneElement(child, { ...dimensions, ...{ getDimensions }, layoutDimensions: layout.dimensions });
    });

    return (
        <View
            ref={containerRef}
            {...props}
            onLayout={layoutCallback}
            style={[dimensions.width && { width: dimensions.width || '100%' }, dimensions.height && { minHeight: dimensions.height || '100%' }, props.style]}
        >
            {containerRefVal && reChildren}
        </View>
    );
};

export default HardDimensionsContainer;
